/**
 * Auth Domain Service
 * 
 * Handles domain discovery, SSO routing, and credential linking
 * for the authorised domains system.
 */

import { 
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getAuth } from 'firebase/auth';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import {
  AuthorisedDomain,
  SSOProvider,
  DomainIndex,
  JoinRequest,
  Invitation,
  DiscoverResponse,
  PostLoginResponse,
  AuthorisedDomainFormData,
  SSOProviderFormData,
  COLLECTIONS,
  extractDomain,
  hashDomain,
  isConsumerDomain,
  generateVerificationToken,
  getVerificationTxtRecordName,
  JoinPolicy,
  AuthMode,
  JoinRequestStatus,
  InvitationStatus,
} from '@/types/authorisedDomains';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// DOMAIN DISCOVERY (Public API)
// ============================================================================

/**
 * Discover tenancy and auth options for an email address
 * This is called on the login page before authentication
 */
export async function discoverDomain(email: string): Promise<DiscoverResponse> {
  try {
    const response = await deiterateAPI.POST<DiscoverResponse>(
      '/auth/discover',
      {
        body: JSON.stringify({ email }),
        backend: 'thalassa',
      }
    );
    return response;
  } catch (error) {
    console.error('[authDomainService] discoverDomain error:', error);
    return {
      found: false,
      availableAuthMethods: ['password', 'magic_link', 'google', 'microsoft'],
      error: 'Failed to discover domain',
    };
  }
}

/**
 * Post-login handler to attach user to tenancy based on domain
 */
export async function handlePostLogin(idToken: string): Promise<PostLoginResponse> {
  try {
    const response = await deiterateAPI.POST<PostLoginResponse>(
      '/auth/postLogin',
      {
        body: JSON.stringify({ idToken }),
        backend: 'thalassa',
      }
    );
    return response;
  } catch (error) {
    console.error('[authDomainService] handlePostLogin error:', error);
    return {
      success: false,
      error: 'Failed to process login',
      errorCode: 'INTERNAL_ERROR',
    };
  }
}

/**
 * Link SSO credential to existing account
 */
export async function linkSSOCredential(
  idToken: string,
  pendingCredential: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await deiterateAPI.POST<{ success: boolean; error?: string }>(
      '/auth/linkSSO',
      {
        body: JSON.stringify({ idToken, pendingCredential }),
        backend: 'thalassa',
      }
    );
    return response;
  } catch (error) {
    console.error('[authDomainService] linkSSOCredential error:', error);
    return {
      success: false,
      error: 'Failed to link SSO credential',
    };
  }
}

// ============================================================================
// AUTHORISED DOMAIN MANAGEMENT (Admin)
// ============================================================================

/**
 * Check if a domain is available for registration
 * Returns the tenancy name if already registered by another tenancy
 */
export async function checkDomainAvailability(
  domain: string,
  currentTenancyId?: string
): Promise<{ available: boolean; registeredBy?: string; error?: string }> {
  try {
    const normalizedDomain = domain.toLowerCase().trim();
    
    // Check if it's a consumer domain
    if (isConsumerDomain(normalizedDomain)) {
      return {
        available: false,
        error: 'Consumer email domains (gmail.com, outlook.com, etc.) cannot be registered.',
      };
    }
    
    // Check the global domain index
    const domainHash = hashDomain(normalizedDomain);
    const indexRef = doc(db, COLLECTIONS.DOMAIN_INDEX, domainHash);
    const existingIndex = await getDoc(indexRef);
    
    if (existingIndex.exists()) {
      const existingData = existingIndex.data() as DomainIndex;
      
      // If it belongs to the current tenancy, it's "available" (already owned)
      if (currentTenancyId && existingData.tenancyId === currentTenancyId) {
        return {
          available: false,
          error: 'This domain is already registered by your organisation.',
        };
      }
      
      // Registered by another tenancy
      return {
        available: false,
        registeredBy: existingData.tenancyName || 'another organisation',
        error: 'This domain is already registered by another organisation.',
      };
    }
    
    return { available: true };
  } catch (error) {
    console.error('[authDomainService] checkDomainAvailability error:', error);
    return { available: false, error: 'Failed to check domain availability' };
  }
}

/**
 * Get all authorised domains for a tenancy
 */
export async function getAuthorisedDomains(tenancyId: string): Promise<AuthorisedDomain[]> {
  try {
    const domainsRef = collection(db, COLLECTIONS.getAuthorisedDomains(tenancyId));
    const snapshot = await getDocs(query(domainsRef, orderBy('createdAt', 'desc')));
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as AuthorisedDomain));
  } catch (error) {
    console.error('[authDomainService] getAuthorisedDomains error:', error);
    return [];
  }
}

/**
 * Get a single authorised domain by ID
 */
export async function getAuthorisedDomain(
  tenancyId: string,
  domainId: string
): Promise<AuthorisedDomain | null> {
  try {
    const domainRef = doc(db, COLLECTIONS.getAuthorisedDomains(tenancyId), domainId);
    const snapshot = await getDoc(domainRef);
    
    if (!snapshot.exists()) return null;
    
    return {
      id: snapshot.id,
      ...snapshot.data(),
    } as AuthorisedDomain;
  } catch (error) {
    console.error('[authDomainService] getAuthorisedDomain error:', error);
    return null;
  }
}

/**
 * Create a new authorised domain
 */
export async function createAuthorisedDomain(
  tenancyId: string,
  tenancyName: string,
  data: AuthorisedDomainFormData,
  createdBy: string
): Promise<{ success: boolean; domain?: AuthorisedDomain; error?: string }> {
  try {
    // Debug: Log user info
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      console.log('[authDomainService] Current user:', user.uid);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('[authDomainService] User data:', {
          organisations: userData.organisations,
          roles: userData.roles,
          mspOrgs: userData.mspOrgs
        });
      }
    }
    const normalizedDomain = data.domain.toLowerCase().trim();
    
    if (isConsumerDomain(normalizedDomain)) {
      return {
        success: false,
        error: 'Consumer email domains (gmail.com, outlook.com, etc.) cannot be registered as authorised domains.',
      };
    }
    
    const domainHash = hashDomain(normalizedDomain);
    const indexRef = doc(db, COLLECTIONS.DOMAIN_INDEX, domainHash);
    const existingIndex = await getDoc(indexRef);
    
    if (existingIndex.exists()) {
      const existingData = existingIndex.data() as DomainIndex;
      if (existingData.tenancyId !== tenancyId) {
        return {
          success: false,
          error: 'This domain is already registered by another organisation.',
        };
      }
    }
    
    const id = uuidv4();
    const verificationToken = generateVerificationToken();
    const now = Timestamp.now();
    
    const authorisedDomain: AuthorisedDomain = {
      id,
      domain: normalizedDomain,
      verified: false,
      verificationStatus: 'unverified',
      verificationToken,
      verificationExpiresAt: Timestamp.fromMillis(now.toMillis() + 7 * 24 * 60 * 60 * 1000), // 7 days
      verificationAttempts: 0,
      joinPolicy: data.joinPolicy,
      authMode: data.authMode,
      defaultRole: data.defaultRole,
      ...(data.ssoProviderId && { ssoProviderId: data.ssoProviderId }),
      createdAt: now,
      createdBy,
      updatedAt: now,
    };
    
    const domainIndex: DomainIndex = {
      domain: normalizedDomain,
      domainHash,
      tenancyId,
      tenancyName,
      verified: false,
      joinPolicy: data.joinPolicy,
      authMode: data.authMode,
      defaultRole: data.defaultRole,
      ...(data.ssoProviderId && { ssoProviderId: data.ssoProviderId }),
      createdAt: now,
      updatedAt: now,
    };
    
    // Double-check availability right before writing (race condition protection)
    const availabilityCheck = await checkDomainAvailability(normalizedDomain, tenancyId);
    if (!availabilityCheck.available) {
      return {
        success: false,
        error: availabilityCheck.error || 'This domain is not available for registration.',
      };
    }
    
    // Write the global index FIRST to claim the domain
    // This prevents race conditions where two tenancies try to add the same domain
    try {
      await setDoc(indexRef, domainIndex);
    } catch (indexError) {
      console.error('[authDomainService] Failed to set system index:', indexError);
      return {
        success: false,
        error: 'Failed to register domain in global index. Please try again.',
      };
    }
    
    // Now write the tenancy-specific domain document
    try {
      const domainRef = doc(db, COLLECTIONS.getAuthorisedDomains(tenancyId), id);
      await setDoc(domainRef, authorisedDomain);
    } catch (domainError) {
      // Rollback the index if domain write fails
      console.error('[authDomainService] Failed to create domain, rolling back index:', domainError);
      try {
        await deleteDoc(indexRef);
      } catch (rollbackError) {
        console.error('[authDomainService] Failed to rollback index:', rollbackError);
      }
      return {
        success: false,
        error: 'Failed to create authorised domain. Please try again.',
      };
    }
    
    return { success: true, domain: authorisedDomain };
  } catch (error) {
    console.error('[authDomainService] createAuthorisedDomain error:', error);
    return {
      success: false,
      error: 'Failed to create authorised domain',
    };
  }
}

/**
 * Update an authorised domain
 */
export async function updateAuthorisedDomain(
  tenancyId: string,
  domainId: string,
  data: Partial<AuthorisedDomainFormData>,
  updatedBy: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const domainRef = doc(db, COLLECTIONS.getAuthorisedDomains(tenancyId), domainId);
    const existingDoc = await getDoc(domainRef);
    
    if (!existingDoc.exists()) {
      return { success: false, error: 'Domain not found' };
    }
    
    const existingDomain = existingDoc.data() as AuthorisedDomain;
    const now = Timestamp.now();
    
    // Build updates object, filtering out undefined values (Firestore doesn't accept undefined)
    const updates: Record<string, any> = {
      updatedAt: now,
      updatedBy,
    };
    
    // Only add defined values from data
    if (data.domain !== undefined) updates.domain = data.domain;
    if (data.joinPolicy !== undefined) updates.joinPolicy = data.joinPolicy;
    if (data.authMode !== undefined) updates.authMode = data.authMode;
    // For ssoProviderId, use deleteField() to remove it if null, otherwise set the value
    if (data.ssoProviderId === null) {
      updates.ssoProviderId = deleteField();
    } else if (data.ssoProviderId !== undefined) {
      updates.ssoProviderId = data.ssoProviderId;
    }
    // Handle firebaseProviderId the same way
    if (data.firebaseProviderId === null) {
      updates.firebaseProviderId = deleteField();
    } else if (data.firebaseProviderId !== undefined) {
      updates.firebaseProviderId = data.firebaseProviderId;
    }
    
    await updateDoc(domainRef, updates);
    
    const domainHash = hashDomain(existingDomain.domain);
    const indexRef = doc(db, COLLECTIONS.DOMAIN_INDEX, domainHash);
    
    const indexUpdates: Record<string, any> = {
      updatedAt: now,
    };
    if (data.joinPolicy) indexUpdates.joinPolicy = data.joinPolicy;
    if (data.authMode) indexUpdates.authMode = data.authMode;
    // Handle ssoProviderId for index - use deleteField() if null
    if (data.ssoProviderId === null) {
      indexUpdates.ssoProviderId = deleteField();
    } else if (data.ssoProviderId !== undefined) {
      indexUpdates.ssoProviderId = data.ssoProviderId;
    }
    // Handle firebaseProviderId for index
    if (data.firebaseProviderId === null) {
      indexUpdates.firebaseProviderId = deleteField();
    } else if (data.firebaseProviderId !== undefined) {
      indexUpdates.firebaseProviderId = data.firebaseProviderId;
    }
    
    await updateDoc(indexRef, indexUpdates);
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] updateAuthorisedDomain error:', error);
    return { success: false, error: 'Failed to update authorised domain' };
  }
}

/**
 * Delete an authorised domain
 */
export async function deleteAuthorisedDomain(
  tenancyId: string,
  domainId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const domainRef = doc(db, COLLECTIONS.getAuthorisedDomains(tenancyId), domainId);
    const existingDoc = await getDoc(domainRef);
    
    if (!existingDoc.exists()) {
      return { success: false, error: 'Domain not found' };
    }
    
    const existingDomain = existingDoc.data() as AuthorisedDomain;
    
    const batch = writeBatch(db);
    
    batch.delete(domainRef);
    
    const domainHash = hashDomain(existingDomain.domain);
    const indexRef = doc(db, COLLECTIONS.DOMAIN_INDEX, domainHash);
    batch.delete(indexRef);
    
    await batch.commit();
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] deleteAuthorisedDomain error:', error);
    return { success: false, error: 'Failed to delete authorised domain' };
  }
}

/**
 * Initiate domain verification
 */
export async function initiateVerification(
  tenancyId: string,
  domainId: string
): Promise<{ success: boolean; txtRecordName?: string; txtRecordValue?: string; error?: string }> {
  try {
    const domain = await getAuthorisedDomain(tenancyId, domainId);
    if (!domain) {
      return { success: false, error: 'Domain not found' };
    }
    
    const verificationToken = generateVerificationToken();
    const now = Timestamp.now();
    
    const domainRef = doc(db, COLLECTIONS.getAuthorisedDomains(tenancyId), domainId);
    await updateDoc(domainRef, {
      verificationToken,
      verificationStatus: 'pending',
      verificationExpiresAt: Timestamp.fromMillis(now.toMillis() + 7 * 24 * 60 * 60 * 1000),
      verificationAttempts: (domain.verificationAttempts || 0) + 1,
      lastVerificationAttempt: now,
      updatedAt: now,
    });
    
    return {
      success: true,
      txtRecordName: getVerificationTxtRecordName(domain.domain),
      txtRecordValue: verificationToken,
    };
  } catch (error) {
    console.error('[authDomainService] initiateVerification error:', error);
    return { success: false, error: 'Failed to initiate verification' };
  }
}

/**
 * Check domain verification status (calls backend to do DNS lookup)
 */
export async function checkVerification(
  tenancyId: string,
  domainId: string
): Promise<{ verified: boolean; error?: string }> {
  try {
    const response = await deiterateAPI.POST<{ verified: boolean; error?: string }>(
      '/auth/checkVerification',
      {
        body: JSON.stringify({ tenancyId, domainId }),
        companyID: tenancyId,
        backend: 'thalassa',
      }
    );
    return response;
  } catch (error) {
    console.error('[authDomainService] checkVerification error:', error);
    return { verified: false, error: 'Failed to check verification' };
  }
}

// ============================================================================
// SSO PROVIDER MANAGEMENT (Admin)
// ============================================================================

/**
 * Get all SSO providers for a tenancy
 */
export async function getSSOProviders(tenancyId: string): Promise<SSOProvider[]> {
  try {
    const providersRef = collection(db, COLLECTIONS.getSSOProviders(tenancyId));
    const snapshot = await getDocs(query(providersRef, orderBy('createdAt', 'desc')));
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as SSOProvider));
  } catch (error) {
    console.error('[authDomainService] getSSOProviders error:', error);
    return [];
  }
}

/**
 * Get a single SSO provider by ID
 */
export async function getSSOProvider(
  tenancyId: string,
  providerId: string
): Promise<SSOProvider | null> {
  try {
    const providerRef = doc(db, COLLECTIONS.getSSOProviders(tenancyId), providerId);
    const snapshot = await getDoc(providerRef);
    
    if (!snapshot.exists()) return null;
    
    return {
      id: snapshot.id,
      ...snapshot.data(),
    } as SSOProvider;
  } catch (error) {
    console.error('[authDomainService] getSSOProvider error:', error);
    return null;
  }
}

/**
 * Create a new SSO provider using Firebase Admin SDK via backend
 */
export async function createSSOProvider(
  tenancyId: string,
  data: SSOProviderFormData,
  createdBy: string
): Promise<{ success: boolean; provider?: SSOProvider; error?: string }> {
  try {
    // Call backend API to create provider in Firebase Auth
    const response = await deiterateAPI.POST<{
      success: boolean;
      providerId?: string;
      firebaseProviderId?: string;
      error?: string;
    }>(
      '/auth/sso/create',
      {
        body: JSON.stringify({
          tenancyId,
          name: data.name,
          displayName: data.displayName,
          type: data.type,
          samlConfig: data.samlConfig,
          oidcConfig: data.oidcConfig,
        }),
        backend: 'thalassa',
      }
    );

    if (!response.success) {
      return { success: false, error: response.error || 'Failed to create SSO provider' };
    }

    // Fetch the created provider from Firestore
    const providerRef = doc(db, COLLECTIONS.getSSOProviders(tenancyId), response.providerId!);
    const providerDoc = await getDoc(providerRef);
    
    if (!providerDoc.exists()) {
      return { success: false, error: 'Provider created but not found in database' };
    }

    const provider = {
      id: providerDoc.id,
      ...providerDoc.data(),
    } as SSOProvider;

    return { success: true, provider };
  } catch (error) {
    console.error('[authDomainService] createSSOProvider error:', error);
    return { success: false, error: 'Failed to create SSO provider' };
  }
}

/**
 * Update an SSO provider
 */
export async function updateSSOProvider(
  tenancyId: string,
  providerId: string,
  data: Partial<SSOProviderFormData>,
  updatedBy: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const providerRef = doc(db, COLLECTIONS.getSSOProviders(tenancyId), providerId);
    const now = Timestamp.now();
    
    await updateDoc(providerRef, {
      ...data,
      updatedAt: now,
      updatedBy,
    });
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] updateSSOProvider error:', error);
    return { success: false, error: 'Failed to update SSO provider' };
  }
}

/**
 * Delete an SSO provider (also removes from Firebase Auth)
 */
export async function deleteSSOProvider(
  tenancyId: string,
  providerId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if any domains are using this provider
    const domainsRef = collection(db, COLLECTIONS.getAuthorisedDomains(tenancyId));
    const domainsUsingProvider = await getDocs(
      query(domainsRef, where('ssoProviderId', '==', providerId))
    );
    
    if (!domainsUsingProvider.empty) {
      return {
        success: false,
        error: `Cannot delete: ${domainsUsingProvider.size} domain(s) are using this SSO provider.`,
      };
    }

    // Get provider to get firebaseProviderId
    const providerRef = doc(db, COLLECTIONS.getSSOProviders(tenancyId), providerId);
    const providerDoc = await getDoc(providerRef);
    
    if (!providerDoc.exists()) {
      return { success: false, error: 'Provider not found' };
    }

    const provider = providerDoc.data();
    
    // Call backend API to delete from Firebase Auth
    const response = await deiterateAPI.POST<{ success: boolean; error?: string }>(
      '/auth/sso/delete',
      {
        body: JSON.stringify({
          tenancyId,
          providerId,
          firebaseProviderId: provider.firebaseProviderId,
        }),
        backend: 'thalassa',
      }
    );

    if (!response.success) {
      return { success: false, error: response.error || 'Failed to delete SSO provider' };
    }
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] deleteSSOProvider error:', error);
    return { success: false, error: 'Failed to delete SSO provider' };
  }
}

// ============================================================================
// JOIN REQUEST MANAGEMENT (Admin)
// ============================================================================

/**
 * Get all pending join requests for a tenancy
 */
export async function getJoinRequests(
  tenancyId: string,
  status?: JoinRequestStatus
): Promise<JoinRequest[]> {
  try {
    const requestsRef = collection(db, COLLECTIONS.getJoinRequests(tenancyId));
    let q = query(requestsRef, orderBy('createdAt', 'desc'));
    
    if (status) {
      q = query(requestsRef, where('status', '==', status), orderBy('createdAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as JoinRequest));
  } catch (error) {
    console.error('[authDomainService] getJoinRequests error:', error);
    return [];
  }
}

/**
 * Approve a join request
 */
export async function approveJoinRequest(
  tenancyId: string,
  requestId: string,
  reviewedBy: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const requestRef = doc(db, COLLECTIONS.getJoinRequests(tenancyId), requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      return { success: false, error: 'Join request not found' };
    }
    
    const request = requestDoc.data() as JoinRequest;
    
    if (request.status !== 'pending') {
      return { success: false, error: 'Join request is no longer pending' };
    }
    
    const now = Timestamp.now();
    
    await updateDoc(requestRef, {
      status: 'approved',
      reviewedBy,
      reviewedAt: now,
    });
    
    const response = await deiterateAPI.POST<{ success: boolean; error?: string }>(
      '/auth/attachUserToTenancy',
      {
        body: JSON.stringify({ userId: request.userId, tenancyId }),
        companyID: tenancyId,
        backend: 'thalassa',
      }
    );
    
    if (!response.success) {
      await updateDoc(requestRef, {
        status: 'pending',
        reviewedBy: null,
        reviewedAt: null,
      });
      return { success: false, error: response.error || 'Failed to attach user to tenancy' };
    }
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] approveJoinRequest error:', error);
    return { success: false, error: 'Failed to approve join request' };
  }
}

/**
 * Reject a join request
 */
export async function rejectJoinRequest(
  tenancyId: string,
  requestId: string,
  reviewedBy: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const requestRef = doc(db, COLLECTIONS.getJoinRequests(tenancyId), requestId);
    const now = Timestamp.now();
    
    await updateDoc(requestRef, {
      status: 'rejected',
      reviewedBy,
      reviewedAt: now,
      rejectionReason: reason,
    });
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] rejectJoinRequest error:', error);
    return { success: false, error: 'Failed to reject join request' };
  }
}

// ============================================================================
// INVITATION MANAGEMENT (Admin)
// ============================================================================

/**
 * Get all invitations for a tenancy
 */
export async function getInvitations(
  tenancyId: string,
  status?: InvitationStatus
): Promise<Invitation[]> {
  try {
    const invitesRef = collection(db, COLLECTIONS.getInvitations(tenancyId));
    let q = query(invitesRef, orderBy('createdAt', 'desc'));
    
    if (status) {
      q = query(invitesRef, where('status', '==', status), orderBy('createdAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Invitation));
  } catch (error) {
    console.error('[authDomainService] getInvitations error:', error);
    return [];
  }
}

/**
 * Create an invitation
 */
export async function createInvitation(
  tenancyId: string,
  email: string,
  invitedBy: string,
  invitedByName: string,
  role?: string,
  expirationDays: number = 7
): Promise<{ success: boolean; invitation?: Invitation; error?: string }> {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    
    const invitesRef = collection(db, COLLECTIONS.getInvitations(tenancyId));
    const existingInvites = await getDocs(
      query(invitesRef, where('email', '==', normalizedEmail), where('status', '==', 'pending'))
    );
    
    if (!existingInvites.empty) {
      return { success: false, error: 'An active invitation already exists for this email' };
    }
    
    const id = uuidv4();
    const now = Timestamp.now();
    
    const invitation: Invitation = {
      id,
      email: normalizedEmail,
      role,
      invitedBy,
      invitedByName,
      expiresAt: Timestamp.fromMillis(now.toMillis() + expirationDays * 24 * 60 * 60 * 1000),
      status: 'pending',
      createdAt: now,
    };
    
    const inviteRef = doc(db, COLLECTIONS.getInvitations(tenancyId), id);
    await setDoc(inviteRef, invitation);
    
    return { success: true, invitation };
  } catch (error) {
    console.error('[authDomainService] createInvitation error:', error);
    return { success: false, error: 'Failed to create invitation' };
  }
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(
  tenancyId: string,
  invitationId: string,
  revokedBy: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const inviteRef = doc(db, COLLECTIONS.getInvitations(tenancyId), invitationId);
    const now = Timestamp.now();
    
    await updateDoc(inviteRef, {
      status: 'revoked',
      revokedAt: now,
      revokedBy,
    });
    
    return { success: true };
  } catch (error) {
    console.error('[authDomainService] revokeInvitation error:', error);
    return { success: false, error: 'Failed to revoke invitation' };
  }
}

/**
 * Check if a valid invitation exists for an email
 */
export async function checkInvitation(
  tenancyId: string,
  email: string
): Promise<{ valid: boolean; invitation?: Invitation }> {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const invitesRef = collection(db, COLLECTIONS.getInvitations(tenancyId));
    const snapshot = await getDocs(
      query(invitesRef, where('email', '==', normalizedEmail), where('status', '==', 'pending'))
    );
    
    if (snapshot.empty) {
      return { valid: false };
    }
    
    const invitation = {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data(),
    } as Invitation;
    
    if (invitation.expiresAt.toMillis() < Date.now()) {
      await updateDoc(doc(db, COLLECTIONS.getInvitations(tenancyId), invitation.id), {
        status: 'expired',
      });
      return { valid: false };
    }
    
    return { valid: true, invitation };
  } catch (error) {
    console.error('[authDomainService] checkInvitation error:', error);
    return { valid: false };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get Firebase Auth SSO provider for sign-in
 */
export async function getFirebaseSSOProvider(
  type: 'saml' | 'oidc',
  providerId: string
): any {
  if (typeof window === 'undefined') return null;
  
  // Import from modular Firebase v9+ for SAML/OIDC support
  try {
    // Dynamic import to avoid SSR issues
    
    const { SAMLAuthProvider, OAuthProvider } = await import('firebase/auth');
    if (type === 'saml') {
      return new SAMLAuthProvider(providerId);
    } else {
      return new OAuthProvider(providerId);
    }
  } catch (error) {
    console.error('[getFirebaseSSOProvider] Error creating provider:', error);
    return null;
  }
}

export default {
  discoverDomain,
  handlePostLogin,
  linkSSOCredential,
  checkDomainAvailability,
  getAuthorisedDomains,
  getAuthorisedDomain,
  createAuthorisedDomain,
  updateAuthorisedDomain,
  deleteAuthorisedDomain,
  initiateVerification,
  checkVerification,
  getSSOProviders,
  getSSOProvider,
  createSSOProvider,
  updateSSOProvider,
  deleteSSOProvider,
  getJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
  getInvitations,
  createInvitation,
  revokeInvitation,
  checkInvitation,
  getFirebaseSSOProvider,
};
